import { FC, useContext, useMemo } from 'react';
import { isArray, isNotEmpty } from '@lhb/func';

import { Col, Row } from 'antd';
import styles from './../index.module.less';
import V2DetailItem from '@/common/components/Feedback/V2DetailItem';
import { ControlType, parseValueCatch } from '@/common/components/business/DynamicComponent/config';
import LocationMap from '@/common/components/business/StoreDetail/components/LocationMap';
import { decodeTextValue } from '@/common/components/business/StoreDetail/components/DynamicDetail/config';
import V2Empty from '@/common/components/Data/V2Empty';
import { ModuleDetailsType } from '../../../type';
import { dispatchNavigate } from '@/common/document-event/dispatch';
import UserInfoContext from '@/layout/context';
import V2Title from '@/common/components/Feedback/V2Title';
import { videoTypes } from '@/common/components/config-v2';
import { getBusinessTermsTypeName, isShowDiscount, isShowRent } from '@/common/components/business/DynamicComponent/components/BusinessTerms/config';

// import Surround from '@/common/components/business/StoreDetail/components/Surround';


/** 核心信息组件传参类型 */
interface CoreInfomationProps {
  data: ModuleDetailsType;
  detailInfoConfig?;
}

/**
 * 机会点详情-核心信息模块
 */
const CoreInfomation: FC<CoreInfomationProps> = ({ data, detailInfoConfig = { span: 12 } }) => {

  const { tenantInfo } = useContext(UserInfoContext); // 租户信息

  /** 不同类型字段*/
  const FieldItem = ({ item }) => {
    const textValue = decodeTextValue(item?.controlType, item?.textValue); // textValue值结构
    const name = item.anotherName || item.name; // 获取表单label
    const { templateRestriction } = item;
    let templateRestrictionVal: any = {};
    // 解析json格式的textValue
    if (templateRestriction) {
      templateRestrictionVal = parseValueCatch(item, 'templateRestriction');
    }
    // 是否是换行显示
    const { nextLine } = templateRestrictionVal;

    switch (item.controlType) {
      // 文件类
      case ControlType.UPLOAD.value:
        // 判断是否全是图片/视频
        const PICTURE_REG = /(\.gif|\.jpeg|\.png|\.jpg|\.bmp|\.svg)$/; // 验证文件是图片格式的正则
        const VIDEO_REG = new RegExp(`(${videoTypes.map(t => `\\.${t}`).join('|')})$`); // 验证文件是视频格式的正则
        const isAllImage = textValue?.every(file => PICTURE_REG.test(file.name));
        const isAllVideo = textValue?.every(file => VIDEO_REG.test(file.name));
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              {isAllImage ? (
                <V2DetailItem
                  label={name}
                  type='images'
                  assets={textValue.filter(item => !!item.url)}
                />
              ) : isAllVideo ? (
                <V2DetailItem
                  label={name}
                  type='videos'
                  assets={textValue.filter(item => !!item.url)}
                />
              ) : (
                <V2DetailItem
                  label={name}
                  type='files'
                  assets={textValue.filter(item => !!item.url)}
                />
              )}
            </Col>
          </>
        );
      // 地图类
      case ControlType.ADDRESS.value:
        return (
          <>
            <Col span={24}></Col>
            <Col span={24}>
              <V2DetailItem label={name} value={textValue?.address} />
            </Col>
            <Col span={24}>
              <V2DetailItem>
                <LocationMap lng={textValue?.lng} lat={textValue?.lat} />
              </V2DetailItem>
            </Col>
          </>
        );
      // 省市区
      case ControlType.AREA.value:
      case ControlType.CUSTOM_AREA.value: // 独立的省市区（不关联商圈）
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem
                label={name}
                value={textValue?.length ? textValue?.map(item => item.name || '-').join('') : ''}
              />
            </Col>
          </>
        );
      // 数字，带后缀
      case ControlType.INPUT_NUMBER.value:
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem
                label={name}
                value={`${isNotEmpty(textValue?.value) ? textValue?.value : '-'} ${
                  textValue?.suffix || ''
                }`}
              />
            </Col>
          </>
        );
      case ControlType.LEASE_TERM.value:
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem
                label={name}
                value={textValue?.startDate && textValue?.endDate ? `${textValue.startDate} 至 ${textValue.endDate}` : ''}
              />
            </Col>
            <Col {...detailInfoConfig}>
              <V2DetailItem label='租期时长' value={textValue?.duration || ''} />
            </Col>
          </>
        );
      case ControlType.PROJECT_SPOT_SELECT.value:
        return (<>
          {nextLine ? <Col span={24}></Col> : null}
          <Col {...detailInfoConfig}><V2DetailItem label='项目名称' value={textValue?.projectName || '-'} /></Col>
          <Col {...detailInfoConfig}><V2DetailItem label='点位名称' value={textValue?.spotName || '-'} /></Col>
        </>);
      case ControlType.BUSINESS_CONDITION.value: {
        const type = textValue?.type;
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem label={name} value={getBusinessTermsTypeName(type) || '-'} />
            </Col>
            {
              isShowRent(type) ? <Col {...detailInfoConfig}>
                <V2DetailItem label='月租金' value={isNotEmpty(textValue?.rent) ? `${textValue.rent}元/月` : '-'} />
              </Col> : null
            }
            {
              isShowDiscount(type) ? <Col {...detailInfoConfig}>
                <V2DetailItem label='扣点比例' value={isNotEmpty(textValue?.discount) ? `${textValue.discount}%` : '-'} />
              </Col> : null
            }
          </>
        );
      }
      // 单选
      case ControlType.SINGLE_RADIO.value:
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem label={name} value={textValue?.name} />
            </Col>
          </>
        );
      // 多选
      case ControlType.CHECK_BOX.value:
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem label={name} value={textValue?.map(value => value.name).join('、')} />
            </Col>
          </>
        );
      case ControlType.TEXT_AREA.value:
        // 多行文本类
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <V2DetailItem label={name} value={textValue} type='textarea' />
            </Col>
          </>
        );
      case ControlType.SURROUND_SEARCH.value:
        const value = JSON.parse(textValue);
        if (value === null) {
          return <></>;
        }
        // 周边查询
        return (
          <>
            <Col span={24}></Col>
            <Col span={24}>
              <V2DetailItem label={name} value={value?.address} />
            </Col>
            <Col span={24}>
              <V2DetailItem>
                <LocationMap lng={value?.lng} lat={value?.lat} />
              </V2DetailItem>
            </Col>
            {isArray(value?.surround) &&
              value?.surround?.length &&
              value?.surround?.map(item => (
                <Col span={12}>
                  <V2DetailItem label={item?.categoryName} value={item?.text} />
                </Col>
              ))}
            <Col span={24}></Col>
          </>
        );
      case ControlType.FOOTPRINT.value: // ui说不展示踩点
      case ControlType.MATCH_BUSINESS_CIRCLE.value:
      case ControlType.ESTIMATE_SALES_YHT.value:
        return <></>;
      default:
        // 文本类
        return (
          <>
            {nextLine ? <Col span={24}></Col> : null}
            <Col {...detailInfoConfig}>
              <InputDetailItem item={item} />
              {/* <V2DetailItem label={name} value={textValue} /> */}
            </Col>
          </>
        );
    }
  };

  /** 链接跳转到拓店任务详情 */
  const clickLink = id => {
    id && dispatchNavigate(`/expandstore/expansiontask?id=${id}`);
  };

  /**
   * 文本类别特殊处理
   * */
  const InputDetailItem = ({ item }) => {
    const textValue = decodeTextValue(item?.controlType, item?.textValue); // textValue值结构
    const name = item.anotherName || item.name; // 获取表单label

    if (item.identification === 'basicChancePointRelationTask') {
      //   拓店任务链接类型
      const { id } = textValue && JSON.parse(textValue);
      return (
        <V2DetailItem
          label={name}
          value='点击查看拓店任务信息'
          type='link'
          onClick={() => clickLink(id)}
        />
      );
    } else {
      return <V2DetailItem label={name} value={textValue} />;
    }
  };

  /**
   * @description 能显示的项目
   *  无任务模板时不显示任务详情入口
   * @return
   */
  const displayItems = useMemo(() => {
    if (!isArray(data?.importModule)) return [];

    const hideIdentifications: string[] = [];
    // 判断是否未启用任务
    if (!tenantInfo.taskTemplateEnable) {
      hideIdentifications.push('basicChancePointRelationTask');
    }
    return hideIdentifications.length ? data.importModule.filter((itm: any) => !hideIdentifications.includes(itm.identification)) : data.importModule;
  }, [data?.importModule]);

  return (
    <div>
      <V2Title
        type='H2'
        text={data?.moduleTypeName}
        divider
        className={'pt-16 pb-10'}
      />
      {isArray(displayItems) && displayItems.length ? (
        <div className={styles.coreInfo}>
          <Row gutter={24}>
            {displayItems.map((field, fieldIndex) => (
              <FieldItem item={field} key={field.name + fieldIndex} />
            ))}
          </Row>
        </div>
      ) : (
        <V2Empty />
      )}

      {/* 产品要求拿掉 */}
      {/* 周边信息 */}
      {/* {detail && <Surround detail={detail} className='mt-16' /> } */}

    </div>
  );
};

export default CoreInfomation;
